import { useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";

interface UseSmoothScrollOptions {
  offset?: number; // Offset for fixed headers (in pixels)
  duration?: number; // Scroll duration in ms
  updateHash?: boolean; // Update URL hash on scroll
}

/**
 * Hook for smooth scrolling to section anchors
 * Handles offset for fixed headers and URL hash updates
 */
export function useSmoothScroll({
  offset = 80, // Default offset for navigation header
  duration = 800,
  updateHash = true,
}: UseSmoothScrollOptions = {}) {
  const location = useLocation();

  /**
   * Smoothly scroll to an element with easing
   */
  const scrollToElement = useCallback(
    (element: HTMLElement, customOffset?: number) => {
      const targetPosition = element.getBoundingClientRect().top + window.scrollY;
      const startPosition = window.scrollY;
      const distance = targetPosition - startPosition - (customOffset ?? offset);
      let startTime: number | null = null;

      // Easing function
      const easeInOutCubic = (t: number): number => {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      };

      const animation = (currentTime: number) => {
        if (startTime === null) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(timeElapsed / duration, 1);
        const ease = easeInOutCubic(progress);

        window.scrollTo(0, startPosition + distance * ease);

        if (timeElapsed < duration) {
          requestAnimationFrame(animation);
        }
      };

      requestAnimationFrame(animation);
    },
    [offset, duration]
  );

  /**
   * Scroll to element by ID
   */
  const scrollToId = useCallback(
    (id: string, customOffset?: number) => {
      const element = document.getElementById(id);
      if (!element) {
        console.warn(`Element with id "${id}" not found`);
        return;
      }

      scrollToElement(element, customOffset);

      // Update URL hash if enabled
      if (updateHash) {
        window.history.pushState(null, "", `#${id}`);
      }
    },
    [scrollToElement, updateHash]
  );

  /**
   * Handle anchor link clicks
   */
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href^="#"]') as HTMLAnchorElement;

      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href || href === "#") return;

      // Prevent default scroll behavior
      e.preventDefault();

      const id = href.substring(1);
      scrollToId(id);
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [scrollToId]);

  /**
   * Handle initial hash on page load
   */
  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const id = hash.substring(1);
        scrollToId(id);
      }, 100);
    }
  }, [location.hash, scrollToId]);

  return {
    scrollToId,
    scrollToElement,
  };
}
